"use client"
import type { CSSProperties } from "react"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Button } from "~/components/primitives/Button/Button"
import { TextWithPill } from "~/components/primitives/TextWithPill/TextWithPill"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { DescriptionBlockInterface } from "./DescriptionBlock.interface"
import s from "./DescriptionBlock.module.scss"

export type DescriptionBlockViewProps = Omit<
  DescriptionBlockInterface,
  "id"
> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

/**
 * Presentational component for DescriptionBlock module.
 * Use this for Storybook stories and testing.
 * The main DescriptionBlock component is an async Server Component that fetches CMS data.
 */
export const DescriptionBlockView = ({
  moduleIndex,
  description,
  pill,
  cta,
  config,
  className,
  style,
}: DescriptionBlockViewProps) => {
  const configProps = getModuleConfigProps(config)
  return (
    <section
      className={cn(
        s.descriptionBlock,
        `stack-module-${moduleIndex}`,
        className,
      )}
      data-module="description-block"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn("container", "default-grid")}>
        <div className={s.content}>
          {description && (
            <FadeIn>
              <AnimateText element="div" className={s.description}>
                <TextWithPill content={description} pill={pill} />
              </AnimateText>
            </FadeIn>
          )}
          {cta && (
            <Button
              cta={{ ...cta, variant: "tertiary" }}
              theme={configProps["data-typography"] || "light"}
              className={s.cta}
            />
          )}
        </div>
      </div>
    </section>
  )
}
